import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ArrowRight, ArrowLeft, Upload, X, ShieldCheck } from "lucide-react";
import { PROJECT_TYPES, BUDGETS, TIMELINES, CONTACT_METHODS, CITIES, track } from "../../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_FILE = 4_000_000; // ~4MB per file
const MAX_FILES = 4;

const empty = {
  name: "", phone: "", email: "", city: "", zip_code: "", preferred_contact: "",
  project_type: "", budget: "", timeline: "", description: "", additional_info: "",
  consent: false, company: "",
};

const emailOk = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const digits = (v) => (v || "").replace(/\D/g, "");
const formatPhone = (v) => {
  const d = digits(v).slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

const Label = ({ children, htmlFor, required }) => (
  <label htmlFor={htmlFor} className="block text-xs font-bold text-[#133955] mb-2 uppercase tracking-wide">
    {children} {required && <span className="text-[#54BCE6]">*</span>}
  </label>
);

export const ConnectionForm = () => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(empty);
  const [files, setFiles] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const started = useRef(false);
  const submitted = useRef(false);
  const utm = useRef({ source: "", utm_source: "", utm_medium: "", utm_campaign: "" });

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    utm.current = {
      source: document.referrer || "direct",
      utm_source: p.get("utm_source") || "",
      utm_medium: p.get("utm_medium") || "",
      utm_campaign: p.get("utm_campaign") || "",
    };
    const onPreselect = (e) => setForm((f) => ({ ...f, project_type: e.detail || "" }));
    window.addEventListener("hpc:preselect", onPreselect);
    const onUnload = () => { if (started.current && !submitted.current) track("form_abandon", { step }); };
    window.addEventListener("beforeunload", onUnload);
    return () => {
      window.removeEventListener("hpc:preselect", onPreselect);
      window.removeEventListener("beforeunload", onUnload);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markStart = () => {
    if (!started.current) { started.current = true; track("form_start"); }
  };

  const set = (k) => (e) => {
    markStart();
    const val = k === "phone" ? formatPhone(e.target.value) : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const onFiles = (e) => {
    markStart();
    const incoming = Array.from(e.target.files || []);
    const next = [...files];
    for (const file of incoming) {
      if (next.length >= MAX_FILES) { toast.error(`Up to ${MAX_FILES} files.`); break; }
      if (file.size > MAX_FILE) { toast.error(`${file.name} is larger than 4MB.`); continue; }
      const reader = new FileReader();
      reader.onload = () => {
        setFiles((prev) => prev.length >= MAX_FILES ? prev : [...prev, {
          filename: file.name, content_type: file.type, size: file.size, data_url: reader.result,
        }]);
      };
      reader.readAsDataURL(file);
    }
    e.target.value = "";
  };

  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const validateStep1 = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name.";
    if (digits(form.phone).length < 10) er.phone = "Enter a valid 10-digit phone number.";
    if (!emailOk(form.email)) er.email = "Enter a valid email address.";
    if (!form.city) er.city = "Select your project city.";
    if (digits(form.zip_code).length !== 5) er.zip_code = "Enter a 5-digit ZIP code.";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const goStep2 = () => {
    if (!validateStep1()) { toast.error("Please fix the highlighted fields."); return; }
    track("form_step1_complete");
    setStep(2);
    document.getElementById("request")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    const er = {};
    if (!form.project_type) er.project_type = "Select a project type.";
    if (!form.description.trim()) er.description = "Tell us a little about the project.";
    if (!form.consent) er.consent = "Please provide consent to continue.";
    setErrors(er);
    if (Object.keys(er).length) { toast.error("Please complete the required fields."); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/project-requests`, { ...form, attachments: files, ...utm.current });
      submitted.current = true;
      track("form_submit_success", { project_type: form.project_type, city: form.city });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setForm(empty); setFiles([]); setErrors({}); setStep(1); setDone(false);
    started.current = false; submitted.current = false;
    document.getElementById("top")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="request" data-testid="request-section" className="bg-[#EAF7FC] py-16 md:py-24 scroll-mt-24">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <p className="text-xs uppercase tracking-[0.24em] font-bold text-[#54BCE6] mb-4">Request a connection</p>
            <h2 className="font-display text-[#133955] font-extrabold tracking-tighter text-4xl md:text-5xl leading-[0.98]">
              Tell us about your project.
            </h2>
            <p className="mt-5 text-base text-[#173042]/75">
              Share a few details and we'll help make the right connection with a local contractor.
            </p>
            <div className="mt-6 flex items-start gap-3 rounded-2xl bg-white border border-[rgba(19,57,85,0.06)] p-5">
              <ShieldCheck size={20} className="text-[#54BCE6] shrink-0 mt-0.5" />
              <p className="text-sm text-[#173042]/75">
                Your project information is used to review your request and communicate about a potential contractor connection.
              </p>
            </div>
          </div>

          <div className="lg:col-span-8">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
              className="rounded-[1.75rem] bg-white p-6 sm:p-9 md:p-10 shadow-[0_16px_48px_rgba(19,57,85,0.10)] border border-[rgba(19,57,85,0.06)]"
            >
              {done ? (
                <div data-testid="form-success" className="py-12 text-center">
                  <div className="mx-auto grid place-items-center w-16 h-16 rounded-full bg-[#54BCE6] mb-6">
                    <CheckCircle2 size={32} className="text-[#133955]" />
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-bold text-[#133955]">Your Project Request Has Been Received</h3>
                  <p className="mt-4 text-[#173042]/75 max-w-md mx-auto">
                    Home Pro Connect will review the information and contact you regarding the next step.
                    Please watch for a phone call, text, or email.
                  </p>
                  <button
                    data-testid="form-return-home"
                    onClick={reset}
                    className="mt-8 rounded-full border-2 border-[#133955] text-[#133955] px-7 py-3 font-bold hover:bg-[#133955] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2"
                  >
                    Return to Home
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="connection-form" noValidate>
                  {/* Progress indicator */}
                  <div className="mb-7">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-bold text-[#133955]" data-testid="form-step-label">
                        Step {step} of 2 — {step === 1 ? "About You" : "About Your Project"}
                      </span>
                      <span className="text-xs font-semibold text-[#173042]/50">{step === 1 ? "50%" : "100%"}</span>
                    </div>
                    <div className="h-1.5 w-full rounded-full bg-[#EAF7FC] overflow-hidden">
                      <motion.div className="h-full bg-[#54BCE6]" initial={false} animate={{ width: step === 1 ? "50%" : "100%" }} transition={{ duration: 0.4 }} />
                    </div>
                  </div>

                  {/* Honeypot */}
                  <input type="text" name="company" value={form.company} onChange={set("company")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />

                  <AnimatePresence mode="wait">
                    {step === 1 ? (
                      <motion.div key="s1" initial={{ opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.25 }} className="space-y-5">
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label htmlFor="f-name" required>Full name</Label>
                            <input id="f-name" data-testid="input-name" className="hpc-input" value={form.name} onChange={set("name")} placeholder="Jane Homeowner" />
                            {errors.name && <p className="text-xs text-red-600 mt-1.5" data-testid="err-name">{errors.name}</p>}
                          </div>
                          <div>
                            <Label htmlFor="f-phone" required>Phone number</Label>
                            <input id="f-phone" data-testid="input-phone" inputMode="tel" className="hpc-input" value={form.phone} onChange={set("phone")} placeholder="(843) 555-0100" />
                            {errors.phone && <p className="text-xs text-red-600 mt-1.5" data-testid="err-phone">{errors.phone}</p>}
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label htmlFor="f-email" required>Email address</Label>
                            <input id="f-email" data-testid="input-email" type="email" className="hpc-input" value={form.email} onChange={set("email")} placeholder="jane@email.com" />
                            {errors.email && <p className="text-xs text-red-600 mt-1.5" data-testid="err-email">{errors.email}</p>}
                          </div>
                          <div>
                            <Label htmlFor="f-city" required>Project city</Label>
                            <select id="f-city" data-testid="select-city" className="hpc-input" value={form.city} onChange={set("city")}>
                              <option value="">Select a city</option>
                              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                              <option value="Other Lowcountry community">Other Lowcountry community</option>
                            </select>
                            {errors.city && <p className="text-xs text-red-600 mt-1.5" data-testid="err-city">{errors.city}</p>}
                          </div>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label htmlFor="f-zip" required>ZIP code</Label>
                            <input id="f-zip" data-testid="input-zip" inputMode="numeric" maxLength={5} className="hpc-input" value={form.zip_code} onChange={(e) => { markStart(); setForm((f) => ({ ...f, zip_code: digits(e.target.value).slice(0, 5) })); setErrors((er) => ({ ...er, zip_code: undefined })); }} placeholder="29483" />
                            {errors.zip_code && <p className="text-xs text-red-600 mt-1.5" data-testid="err-zip">{errors.zip_code}</p>}
                          </div>
                          <div>
                            <Label>Preferred contact method</Label>
                            <div className="flex gap-2" role="group" aria-label="Preferred contact method">
                              {CONTACT_METHODS.map((m) => (
                                <button
                                  key={m}
                                  type="button"
                                  data-testid={`contact-${m.toLowerCase()}`}
                                  onClick={() => { markStart(); setForm((f) => ({ ...f, preferred_contact: m })); }}
                                  className={`flex-1 rounded-xl px-3 py-3 text-sm font-bold transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] ${form.preferred_contact === m ? "bg-[#133955] text-white" : "bg-[#EAF7FC] text-[#133955] hover:bg-[#dceff8]"}`}
                                  aria-pressed={form.preferred_contact === m}
                                >
                                  {m}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>

                        <button
                          type="button"
                          data-testid="form-continue"
                          onClick={goStep2}
                          className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
                        >
                          Continue to Project Details
                          <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div key="s2" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 12 }} transition={{ duration: 0.25 }} className="space-y-5">
                        <div>
                          <Label htmlFor="f-type" required>Project type</Label>
                          <select id="f-type" data-testid="select-project-type" className="hpc-input" value={form.project_type} onChange={set("project_type")}>
                            <option value="">Select a project type</option>
                            {PROJECT_TYPES.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                          {errors.project_type && <p className="text-xs text-red-600 mt-1.5" data-testid="err-type">{errors.project_type}</p>}
                        </div>
                        <div className="grid sm:grid-cols-2 gap-5">
                          <div>
                            <Label htmlFor="f-budget">Budget range</Label>
                            <select id="f-budget" data-testid="select-budget" className="hpc-input" value={form.budget} onChange={set("budget")}>
                              <option value="">Select a range</option>
                              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                            </select>
                          </div>
                          <div>
                            <Label htmlFor="f-timeline">Desired timeline</Label>
                            <select id="f-timeline" data-testid="select-timeline" className="hpc-input" value={form.timeline} onChange={set("timeline")}>
                              <option value="">Select a timeline</option>
                              {TIMELINES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                        </div>
                        <div>
                          <Label htmlFor="f-desc" required>Project description</Label>
                          <textarea id="f-desc" data-testid="input-description" rows={4} className="hpc-input resize-none" value={form.description} onChange={set("description")} placeholder="Tell us what you're planning — scope, rooms, goals, and anything else that helps." />
                          {errors.description && <p className="text-xs text-red-600 mt-1.5" data-testid="err-description">{errors.description}</p>}
                        </div>

                        <div>
                          <Label>Photos or plans (optional)</Label>
                          <label data-testid="file-dropzone" className="flex items-center justify-center gap-2 rounded-xl border-2 border-dashed border-[rgba(19,57,85,0.2)] bg-[#EAF7FC] px-4 py-6 cursor-pointer hover:border-[#54BCE6] transition-colors">
                            <Upload size={18} className="text-[#133955]" />
                            <span className="text-sm font-semibold text-[#133955]">Add photos, drawings, or plans</span>
                            <input type="file" data-testid="input-files" multiple accept="image/*,.pdf" onChange={onFiles} className="hidden" />
                          </label>
                          {files.length > 0 && (
                            <div className="mt-3 flex flex-wrap gap-2" data-testid="file-list">
                              {files.map((f, i) => (
                                <span key={i} className="inline-flex items-center gap-2 rounded-full bg-[#EAF7FC] border border-[rgba(19,57,85,0.1)] pl-3 pr-1.5 py-1.5 text-xs font-semibold text-[#133955]">
                                  <span className="max-w-[160px] truncate">{f.filename}</span>
                                  <button type="button" data-testid={`file-remove-${i}`} onClick={() => removeFile(i)} className="grid place-items-center w-5 h-5 rounded-full bg-white hover:bg-red-100" aria-label={`Remove ${f.filename}`}>
                                    <X size={12} />
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                        </div>

                        <div>
                          <Label htmlFor="f-additional">Additional information</Label>
                          <textarea id="f-additional" data-testid="input-additional" rows={2} className="hpc-input resize-none" value={form.additional_info} onChange={set("additional_info")} placeholder="Anything else we should know?" />
                        </div>

                        <label className="flex items-start gap-3 cursor-pointer" data-testid="consent-label">
                          <input type="checkbox" data-testid="input-consent" checked={form.consent} onChange={(e) => { setForm((f) => ({ ...f, consent: e.target.checked })); setErrors((er) => ({ ...er, consent: undefined })); }} className="mt-1 h-5 w-5 rounded border-[#133955] accent-[#133955] shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6]" />
                          <span className="text-sm text-[#173042]/80">
                            I agree that Home Pro Connect and a contractor connected to my request may contact me by phone, email, or text regarding this project.
                          </span>
                        </label>
                        {errors.consent && <p className="text-xs text-red-600 -mt-2" data-testid="err-consent">{errors.consent}</p>}

                        <div className="flex flex-wrap items-center gap-3 pt-1">
                          <button
                            type="button"
                            data-testid="form-back"
                            onClick={() => setStep(1)}
                            className="inline-flex items-center gap-2 rounded-full border-2 border-[#133955] text-[#133955] px-6 py-4 font-bold hover:bg-[#133955] hover:text-white transition-colors duration-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2"
                          >
                            <ArrowLeft size={18} /> Back
                          </button>
                          <button
                            type="submit"
                            data-testid="form-submit"
                            disabled={loading}
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#133955] text-white px-8 py-4 font-bold hover:bg-[#54BCE6] hover:text-[#133955] transition-colors duration-300 disabled:opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#133955] focus-visible:ring-offset-2"
                          >
                            {loading && <Loader2 size={18} className="animate-spin" />}
                            {loading ? "Sending..." : "Request My Contractor Connection"}
                            {!loading && <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />}
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
